import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Loader2, Phone } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = {
  pac: ["Quelles aides pour remplacer ma chaudière fioul ?", "Combien de temps durent les travaux ?", "Intervenez-vous chez moi ?"],
  clim: ["Combien coûte une clim Daikin posée ?", "En combien de temps êtes-vous disponibles ?", "Une clim réversible chauffe-t-elle bien l'hiver ?"],
};

const THEMES = {
  dark: {
    bubble: "bg-emerald-400 text-forest-deep hover:bg-emerald-300",
    panel: "border-white/10 bg-forest-dark text-white",
    header: "border-white/10",
    user: "bg-emerald-400 text-forest-deep",
    bot: "bg-white/10 text-white/90",
    input: "border-white/15 bg-white/5 text-white placeholder-white/40 focus:border-emerald-400",
    send: "bg-emerald-400 text-forest-deep",
    chip: "border-white/15 text-white/70 hover:border-emerald-400 hover:text-white",
    cta: "text-emerald-300",
    ctaId: "#simulateur",
  },
  light: {
    bubble: "bg-ice text-white hover:bg-ice-hover",
    panel: "border-glacier-border bg-white text-ink",
    header: "border-glacier-border",
    user: "bg-ice text-white",
    bot: "bg-glacier text-slate-700",
    input: "border-glacier-border bg-white text-ink placeholder-slate-400 focus:border-ice",
    send: "bg-ice text-white",
    chip: "border-glacier-border text-slate-600 hover:border-ice hover:text-ice",
    cta: "text-ice",
    ctaId: "#devis",
  },
};

const sid = () => {
  let s = sessionStorage.getItem("es_chat_sid");
  if (!s) {
    s = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem("es_chat_sid", s);
  }
  return s;
};

export default function ChatWidget({ product = "pac", theme = "dark" }) {
  const t = THEMES[theme] || THEMES.dark;
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: product === "clim"
      ? "Bonjour ! Une question sur la climatisation Daikin, les prix ou les délais ? Je vous réponds tout de suite."
      : "Bonjour ! Une question sur les aides 2026, la pompe à chaleur ou nos délais ? Je vous réponds tout de suite." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sid(), message: msg, page: product }),
      });
      if (!res.ok || !res.body) throw new Error("http");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const ev = JSON.parse(p.slice(6));
          if (ev.t || ev.error) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + (ev.t || ev.error) };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Désolé, je n'arrive pas à répondre pour le moment. Appelez-nous au 05 35 75 98 03." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25 }}
            className={`fixed bottom-24 right-4 z-[60] flex h-[min(560px,calc(100vh-7rem))] w-[calc(100vw-2rem)] max-w-[380px] flex-col overflow-hidden rounded-3xl border shadow-2xl md:bottom-24 md:right-6 ${t.panel}`}
          >
            <div className={`flex items-center justify-between border-b px-5 py-4 ${t.header}`}>
              <div>
                <p className="font-display text-sm font-bold">Assistant Ener Solutions</p>
                <p className="text-[11px] opacity-60">Répond en quelques secondes · IA</p>
              </div>
              <button data-testid="chat-close" onClick={() => setOpen(false)} className="grid h-8 w-8 place-items-center rounded-full opacity-60 hover:opacity-100" aria-label="Fermer">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`chat-message-${m.role}-${i}`}
                    className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? t.user : t.bot}`}
                  >
                    {m.content || (busy && i === messages.length - 1 ? <Loader2 className="h-4 w-4 animate-spin opacity-60" /> : "")}
                  </div>
                </div>
              ))}
              {messages.length === 1 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {SUGGESTIONS[product].map((s) => (
                    <button key={s} data-testid="chat-suggestion" onClick={() => send(s)} className={`rounded-full border px-3 py-1.5 text-left text-xs transition-colors ${t.chip}`}>
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className={`border-t px-4 py-3 ${t.header}`}>
              <div className="flex items-center gap-2">
                <input
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Votre question…"
                  maxLength={1000}
                  className={`flex-1 rounded-full border px-4 py-2.5 text-sm outline-none transition-colors ${t.input}`}
                />
                <button data-testid="chat-send" onClick={() => send()} disabled={busy || !input.trim()} className={`grid h-10 w-10 shrink-0 place-items-center rounded-full disabled:opacity-40 ${t.send}`} aria-label="Envoyer">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </button>
              </div>
              <div className="mt-2 flex items-center justify-between text-[11px] opacity-70">
                <button data-testid="chat-cta-simulator" onClick={() => { setOpen(false); scrollToId(t.ctaId); }} className={`font-semibold underline underline-offset-2 ${t.cta}`}>
                  {product === "clim" ? "Devis gratuit →" : "Tester mon éligibilité →"}
                </button>
                <a href="tel:+33535759803" className="flex items-center gap-1 font-semibold"><Phone className="h-3 w-3" /> 05 35 75 98 03</a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        data-testid="chat-bubble"
        onClick={() => setOpen((o) => !o)}
        aria-label="Ouvrir l'assistant"
        className={`fixed right-4 z-[60] flex h-14 items-center gap-2 rounded-full px-4 shadow-2xl transition-all hover:scale-105 md:right-6 ${theme === "light" ? "bottom-24 md:bottom-6" : "bottom-6"} ${t.bubble}`}
      >
        {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
        <span className="hidden text-sm font-bold sm:inline">{open ? "Fermer" : "Une question ?"}</span>
      </button>
    </>
  );
}
